import {
  RefreshCcw,
  Search,
  Sparkles,
  TrendingUp,
  UserCheck,
  Users,
} from 'lucide-react';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import type { IEmployeeCustomerMap } from '../types';

interface EmployeeListProps {
  employees: IEmployeeCustomerMap[];
  isLoading: boolean;
  selectedEmployee: IEmployeeCustomerMap | null;
  onEmployeeSelect: (employee: IEmployeeCustomerMap) => void;
  avatarServerPath: string;
  onRefresh?: () => void;
}

// Memoized Employee Item Component
const EmployeeItem = memo<{
  employee: IEmployeeCustomerMap;
  isSelected: boolean;
  isFocused: boolean;
  avatarServerPath: string;
  onClick: (employee: IEmployeeCustomerMap) => void;
}>(({ employee, isSelected, isFocused, avatarServerPath, onClick }) => {
  const handleClick = useCallback(() => {
    onClick(employee);
  }, [employee, onClick]);

  const avatarFallback = useMemo(() => {
    return employee.empName
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .slice(0, 2);
  }, [employee.empName]);

  const avatarSrc = useMemo(() => {
    return employee.avatar
      ? `${avatarServerPath}${employee.avatar}`
      : undefined;
  }, [employee.avatar, avatarServerPath]);

  return (
    <div
      data-employee-item
      onClick={handleClick}
      className={`group relative cursor-pointer rounded-2xl border p-4 transition-all duration-300 hover:shadow-2xl ${
        isSelected
          ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50 shadow-lg dark:from-blue-900/30 dark:to-purple-900/30'
          : isFocused
            ? 'border-blue-400 bg-gradient-to-r from-blue-100 to-purple-100 shadow-lg dark:from-blue-800/30 dark:to-purple-800/30'
            : 'border-slate-200/50 bg-white/50 hover:border-blue-300 dark:border-slate-700/50 dark:bg-slate-700/50 dark:hover:border-blue-600'
      }`}
    >
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
      <div className="relative flex items-center space-x-4 rtl:space-x-reverse">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 opacity-75 blur-lg transition-opacity duration-300 group-hover:opacity-100"></div>
          <Avatar className="relative h-16 w-16 border-2 border-white shadow-lg dark:border-slate-700">
            <AvatarImage src={avatarSrc} alt={employee.empName} />
            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 font-semibold text-white">
              {avatarFallback}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-slate-900 transition-colors duration-300 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
            {employee.empName}
          </p>
          <p className="truncate text-sm text-slate-600 dark:text-slate-300">
            {employee.jobName}
          </p>
          <div className="mt-2 flex items-center space-x-2 rtl:space-x-reverse">
            <Badge
              variant="soft"
              className="border-blue-200 bg-gradient-to-r from-blue-100 to-purple-100 text-xs text-blue-700 dark:border-blue-700 dark:from-blue-900/50 dark:to-purple-900/50 dark:text-blue-300"
            >
              {employee.departmentName}
            </Badge>
            {employee.allowClientSharing && (
              <Badge
                variant="outline"
                className="border-green-300 text-xs text-green-700 dark:border-green-600 dark:text-green-300"
              >
                <UserCheck className="mr-1 h-3 w-3 rtl:ml-1 rtl:mr-0" />
                مشاركة العملاء
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

EmployeeItem.displayName = 'EmployeeItem';

// Memoized Search Input Component
const SearchInput = memo<{
  value: string;
  onChange: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}>(({ value, onChange, onFocus, onBlur, onKeyDown, inputRef }) => (
  <div className="group relative">
    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-lg transition-all duration-300 group-hover:blur-xl"></div>
    <div className="relative">
      <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-slate-400 transition-colors duration-300 group-hover:text-blue-500" />
      <Input
        ref={inputRef}
        placeholder="البحث بالاسم..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className="h-12 rounded-xl border-white/30 bg-white/50 pl-12 pr-4 backdrop-blur-sm transition-all duration-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/50 dark:border-slate-600/30 dark:bg-slate-700/50"
      />
    </div>
  </div>
));

SearchInput.displayName = 'SearchInput';

// Memoized Department Filter Component
const DepartmentFilter = memo<{
  value: string;
  onChange: (value: string) => void;
  departments: string[];
}>(({ value, onChange, departments }) => (
  <div className="group relative">
    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/20 to-indigo-500/20 blur-lg transition-all duration-300 group-hover:blur-xl"></div>
    <div className="relative">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-12 rounded-xl border-white/30 bg-white/50 backdrop-blur-sm transition-all duration-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 dark:border-slate-600/30 dark:bg-slate-700/50">
          <SelectValue placeholder="تصفية حسب القسم" />
        </SelectTrigger>
        <SelectContent className="border-white/30 bg-white/90 backdrop-blur-xl dark:border-slate-700/30 dark:bg-slate-800/90">
          <SelectItem value="all">جميع الأقسام</SelectItem>
          {departments.map((dept: string) => (
            <SelectItem key={dept} value={dept}>
              {dept}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  </div>
));

DepartmentFilter.displayName = 'DepartmentFilter';

// Memoized Loading Component
const LoadingSpinner = memo(() => (
  <div className="flex items-center justify-center py-12">
    <div className="relative">
      <div className="absolute inset-0 animate-pulse rounded-full bg-gradient-to-r from-blue-500 to-purple-600 blur-lg"></div>
      <div className="relative h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
    </div>
  </div>
));

LoadingSpinner.displayName = 'LoadingSpinner';

// Memoized Empty State Component
const EmptyState = memo(() => (
  <div className="flex items-center justify-center py-16">
    <div className="text-center">
      <div className="mb-6 flex justify-center">
        <div className="relative">
          {/* Subtle animated glow effect */}
          <div className="absolute inset-0 animate-pulse rounded-full bg-gradient-to-br from-blue-500/15 via-purple-500/15 to-indigo-500/15 blur-2xl"></div>
          <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-50 shadow-lg ring-1 ring-slate-200/60 dark:from-slate-700 dark:via-slate-600 dark:to-slate-500 dark:ring-slate-600/60">
            {/* Inner gradient overlay */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/5"></div>
            <Users className="relative h-12 w-12 text-slate-400 drop-shadow-sm transition-all duration-300 dark:text-slate-300" />
          </div>
        </div>
      </div>
      <h3 className="text-xl font-bold text-slate-700 dark:text-slate-200">
        اختر موظفاً
      </h3>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        اختر موظفاً من القائمة لعرض تفاصيل توزيع العملاء
      </p>
    </div>
  </div>
));

EmptyState.displayName = 'EmptyState';

// Main Component with React 19 optimizations
export default function EmployeeList({
  employees,
  isLoading,
  selectedEmployee,
  onEmployeeSelect,
  avatarServerPath,
  onRefresh,
}: EmployeeListProps) {
  // State with concurrent features
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [, startTransition] = useTransition();

  // Refs
  const listContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Handle refresh with loading state
  const handleRefresh = useCallback(async () => {
    if (!onRefresh || isRefreshing) return;

    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh, isRefreshing]);

  // Memoized filtered employees with debounced search
  const filteredEmployees = useMemo(() => {
    return employees.filter((emp: IEmployeeCustomerMap) => {
      const matchesSearch = emp.empName
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesDepartment =
        departmentFilter === 'all' || emp.departmentName === departmentFilter;
      return matchesSearch && matchesDepartment;
    });
  }, [employees, searchQuery, departmentFilter]);

  // Memoized unique departments
  const departments = useMemo(() => {
    return [
      ...new Set(
        employees.map((emp: IEmployeeCustomerMap) => emp.departmentName),
      ),
    ].sort() as string[];
  }, [employees]);

  // Optimized event handlers with useCallback
  const handleSearchChange = useCallback((value: string) => {
    startTransition(() => {
      setSearchQuery(value);
    });
  }, []);

  const handleDepartmentChange = useCallback((value: string) => {
    startTransition(() => {
      setDepartmentFilter(value);
    });
  }, []);

  const handleEmployeeSelect = useCallback(
    (employee: IEmployeeCustomerMap) => {
      onEmployeeSelect(employee);
      setFocusedIndex(-1);
      setIsSearchFocused(false);
    },
    [onEmployeeSelect],
  );

  const handleSearchFocus = useCallback(() => {
    setIsSearchFocused(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    setTimeout(() => setIsSearchFocused(false), 200);
  }, []);

  // Optimized scroll function
  const scrollToFocusedItem = useCallback((index: number) => {
    if (!listContainerRef.current) return;

    const container = listContainerRef.current;
    const items = container.querySelectorAll('[data-employee-item]');
    const focusedItem = items[index] as HTMLElement;

    if (focusedItem) {
      const containerRect = container.getBoundingClientRect();
      const itemRect = focusedItem.getBoundingClientRect();

      if (itemRect.top < containerRect.top) {
        container.scrollTop -= containerRect.top - itemRect.top + 20;
      } else if (itemRect.bottom > containerRect.bottom) {
        container.scrollTop += itemRect.bottom - containerRect.bottom + 20;
      }
    }
  }, []);

  // Optimized keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isSearchFocused) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const newIndex = prev < filteredEmployees.length - 1 ? prev + 1 : 0;
            setTimeout(() => scrollToFocusedItem(newIndex), 0);
            return newIndex;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : filteredEmployees.length - 1;
            setTimeout(() => scrollToFocusedItem(newIndex), 0);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < filteredEmployees.length) {
            handleEmployeeSelect(filteredEmployees[focusedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setFocusedIndex(-1);
          setIsSearchFocused(false);
          break;
      }
    },
    [
      isSearchFocused,
      filteredEmployees,
      focusedIndex,
      handleEmployeeSelect,
      scrollToFocusedItem,
    ],
  );

  // Effects
  useEffect(() => {
    setFocusedIndex(-1);
  }, [searchQuery, departmentFilter]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Memoized header content
  const headerContent = useMemo(
    () => (
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          <div className="relative">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 opacity-75 blur-md"></div>
            <div className="relative rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 p-2.5 shadow-lg">
              <Users className="h-5 w-5 text-white" />
            </div>
          </div>
          <div className="space-y-1">
            <CardTitle className="bg-gradient-to-r from-slate-900 via-blue-800 to-purple-800 bg-clip-text text-lg font-bold text-transparent dark:from-white dark:via-blue-200 dark:to-purple-200">
              توزيع العملاء علي الموظفين
            </CardTitle>
            <CardDescription className="text-xs text-slate-600 dark:text-slate-300">
              إدارة وتوزيع العملاء على الموظفين حسب المناطق
            </CardDescription>
          </div>
        </div>
        <div className="flex items-center space-x-3 rtl:space-x-reverse">
          {onRefresh && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRefresh}
                    disabled={isLoading || isRefreshing}
                    className="group relative h-9 w-9 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 p-0 transition-all duration-300 hover:scale-105 hover:from-blue-100 hover:to-purple-100 hover:shadow-lg dark:from-blue-900/20 dark:to-purple-900/20 dark:hover:from-blue-800/30 dark:hover:to-purple-800/30"
                  >
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                    <RefreshCcw
                      className={`relative h-4 w-4 text-blue-600 transition-all duration-300 ${
                        isLoading || isRefreshing
                          ? 'animate-spin'
                          : 'group-hover:scale-110'
                      } dark:text-blue-400`}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="bg-slate-900 text-white"
                >
                  <p className="text-xs font-medium">تحديث قائمة الموظفين</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <div className="flex items-center space-x-1 rounded-lg bg-gradient-to-r from-yellow-50 to-orange-50 px-2 py-1 text-xs text-slate-600 dark:from-yellow-900/20 dark:to-orange-900/20 dark:text-slate-300 rtl:space-x-reverse">
              <Sparkles className="h-3 w-3 text-yellow-500" />
              <span className="font-medium">نظام ذكي</span>
            </div>
            <div className="flex items-center space-x-1 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 px-2 py-1 text-xs text-slate-600 dark:from-green-900/20 dark:to-emerald-900/20 dark:text-slate-300 rtl:space-x-reverse">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="font-medium">تحسين الأداء</span>
            </div>
          </div>
        </div>
      </div>
    ),
    [isLoading, onRefresh, isRefreshing, handleRefresh],
  );

  // Memoized search and filter section
  const searchAndFilterSection = useMemo(
    () => (
      <div className="space-y-4">
        <SearchInput
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          onKeyDown={handleKeyDown}
          inputRef={searchInputRef}
        />
        <DepartmentFilter
          value={departmentFilter}
          onChange={handleDepartmentChange}
          departments={departments}
        />
      </div>
    ),
    [
      searchQuery,
      departmentFilter,
      departments,
      handleSearchChange,
      handleDepartmentChange,
      handleSearchFocus,
      handleSearchBlur,
      handleKeyDown,
    ],
  );

  // Memoized employee list content
  const employeeListContent = useMemo(() => {
    if (isLoading || isRefreshing) {
      return <LoadingSpinner />;
    }

    if (filteredEmployees.length === 0) {
      return <EmptyState />;
    }

    return (
      <div className="space-y-3">
        {filteredEmployees.map(
          (employee: IEmployeeCustomerMap, index: number) => (
            <EmployeeItem
              key={employee._id}
              employee={employee}
              isSelected={selectedEmployee?._id === employee._id}
              isFocused={focusedIndex === index && isSearchFocused}
              avatarServerPath={avatarServerPath}
              onClick={handleEmployeeSelect}
            />
          ),
        )}
      </div>
    );
  }, [
    filteredEmployees,
    isLoading,
    isRefreshing,
    selectedEmployee,
    focusedIndex,
    isSearchFocused,
    avatarServerPath,
    handleEmployeeSelect,
  ]);

  return (
    <Card className="hover:shadow-3xl border-white/20 bg-white/80 shadow-2xl backdrop-blur-xl transition-all duration-500 dark:border-slate-700/50 dark:bg-slate-800/80">
      {isRefreshing && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm dark:bg-slate-800/80">
          <div className="flex items-center space-x-2 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 px-4 py-2 shadow-lg dark:from-blue-900/20 dark:to-purple-900/20 rtl:space-x-reverse">
            <RefreshCcw className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
              تحديث البيانات...
            </span>
          </div>
        </div>
      )}
      <CardHeader className="pb-4">{headerContent}</CardHeader>
      <CardContent className="space-y-6">
        {searchAndFilterSection}
        <div
          ref={listContainerRef}
          className="custom-scrollbar max-h-[900px] overflow-y-auto p-2"
        >
          {employeeListContent}
        </div>
      </CardContent>
    </Card>
  );
}
